import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tabs,
    Tab,
    Chip,
    IconButton,
    Snackbar,
    Alert,
    Paper,
    Divider,
    Collapse,
    Grid
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CardGiftcard as GiftCardIcon,
    ContentCopy as CopyIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import {
    getGiftCards,
    createGiftCard,
    updateGiftCard,
    deleteGiftCard
} from '../api/giftCardApi';
import { getPlans } from '../api/planApi';
import { getAffiliateById } from '../api/affiliateApi';
import { formatCurrency } from '../utils/currency';

const GiftCardManagement = ({ affiliateId }) => {
    const [giftCards, setGiftCards] = useState([]);
    const [plans, setPlans] = useState([]);
    const [paymentCurrency, setPaymentCurrency] = useState('EUR');
    const [filteredCards, setFilteredCards] = useState([]);
    const [activeTab, setActiveTab] = useState(0); // 0: active, 1: redeemed, 2: expired
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null); // For mobile expandable rows
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const [formData, setFormData] = useState({
        name: '',
        amount: '',
        validUntil: '',
        code: '',
        autoGenerateCode: true,
        redemptionMode: '', // amount | plan
        selectedPlanId: ''
    });

    useEffect(() => {
        fetchGiftCards();
        fetchPlans();
        fetchAffiliateCurrency();
    }, [affiliateId]);

    useEffect(() => {
        filterCards();
    }, [activeTab, giftCards]);

    const fetchGiftCards = async () => {
        try {
            const data = await getGiftCards(affiliateId);
            setGiftCards(data);
        } catch (error) {
            showSnackbar('Failed to fetch gift cards', 'error');
        }
    };

    const fetchPlans = async () => {
        try {
            const data = await getPlans(affiliateId);
            const affiliatePlans = Array.isArray(data)
                ? data.filter((plan) => Number(plan.affiliateId) === Number(affiliateId) && plan.active)
                : [];
            setPlans(affiliatePlans);
        } catch (error) {
            showSnackbar('Failed to fetch plans', 'error');
        }
    };

    const fetchAffiliateCurrency = async () => {
        if (!affiliateId) return;
        try {
            const affiliate = await getAffiliateById(affiliateId);
            setPaymentCurrency(affiliate?.paymentCurrency || 'EUR');
        } catch (error) {
            setPaymentCurrency('EUR');
        }
    };

    const formatMoney = (value) => formatCurrency(value, paymentCurrency);

    const filterCards = () => {
        let filtered = [];
        const now = new Date();
        
        switch (activeTab) {
            case 0: // Active
                filtered = giftCards.filter(card => 
                    card.status === 'active' && new Date(card.validUntil) > now
                );
                break;
            case 1: // Redeemed
                filtered = giftCards.filter(card => card.status === 'redeemed');
                break;
            case 2: // Expired
                filtered = giftCards.filter(card => 
                    card.status === 'active' && new Date(card.validUntil) <= now
                );
                break;
            default:
                filtered = giftCards;
        }
        
        setFilteredCards(filtered);
    };

    const handleOpenDialog = (card = null) => {
        if (card) {
            setEditMode(true);
            setSelectedCard(card);
            setFormData({
                name: card.name,
                amount: card.amount,
                validUntil: new Date(card.validUntil).toISOString().split('T')[0],
                code: card.code,
                autoGenerateCode: false,
                redemptionMode: (card.redeemablePlans || []).length > 0 ? 'plan' : 'amount',
                selectedPlanId: (card.redeemablePlans || [])[0]?.plan?.id || ''
            });
        } else {
            setEditMode(false);
            setSelectedCard(null);
            setFormData({
                name: '',
                amount: '',
                validUntil: '',
                code: '',
                autoGenerateCode: true,
                redemptionMode: '',
                selectedPlanId: ''
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditMode(false);
        setSelectedCard(null);
        setFormData({
            name: '',
            amount: '',
            validUntil: '',
            code: '',
            autoGenerateCode: true,
            redemptionMode: '',
            selectedPlanId: ''
        });
    };

    const handleSubmit = async () => {
        try {
            const selectedPlan = plans.find((plan) => Number(plan.id) === Number(formData.selectedPlanId));
            const dataToSend = {
                name: formData.name,
                amount: formData.redemptionMode === 'amount' ? formData.amount : (selectedPlan ? selectedPlan.price : undefined),
                validUntil: formData.validUntil,
                affiliateId,
                redeemablePlanIds: formData.redemptionMode === 'plan' && selectedPlan ? [selectedPlan.id] : []
            };

            // Only include code if not auto-generating
            if (!formData.autoGenerateCode && formData.code) {
                dataToSend.code = formData.code.toUpperCase();
            }

            if (editMode && selectedCard) {
                await updateGiftCard(selectedCard.id, dataToSend);
                showSnackbar('Gift card updated successfully', 'success');
            } else {
                await createGiftCard(dataToSend);
                showSnackbar('Gift card created successfully', 'success');
            }
            fetchGiftCards();
            handleCloseDialog();
        } catch (error) {
            showSnackbar(
                error.response?.data?.error || 'Failed to save gift card',
                'error'
            );
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this gift card?')) {
            try {
                await deleteGiftCard(id);
                showSnackbar('Gift card deleted successfully', 'success');
                fetchGiftCards();
            } catch (error) {
                showSnackbar(
                    error.response?.data?.error || 'Failed to delete gift card',
                    'error'
                );
            }
        }
    };

    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code);
        showSnackbar('Code copied to clipboard', 'success');
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleRowClick = (cardId) => {
        setExpandedRow(expandedRow === cardId ? null : cardId);
    };

    const getStatusChip = (card) => {
        const now = new Date();
        const validUntil = new Date(card.validUntil);
        
        if (card.status === 'redeemed') {
            return <Chip label="Redeemed" color="default" size="small" />;
        }
        if (validUntil < now) {
            return <Chip label="Expired" color="error" size="small" />;
        }
        return <Chip label="Active" color="success" size="small" />;
    };

    const getRedeemablePlansLabel = (card) => {
        const plansList = (card.redeemablePlans || []).map((item) => item.plan).filter(Boolean);
        if (plansList.length === 0) {
            return 'Credit only';
        }
        if (plansList.length === 1) {
            return plansList[0].name;
        }
        return `${plansList.length} plans`;
    };

    const isFormValid = () => {
        const baseValid = formData.name && formData.validUntil;
        if (!formData.redemptionMode) {
            return false;
        }
        const modeValid = formData.redemptionMode === 'amount'
            ? Number(formData.amount) > 0
            : !!formData.selectedPlanId;
        if (editMode) {
            return baseValid && modeValid;
        }
        // For new cards, if not auto-generating, code must be provided
        if (!formData.autoGenerateCode) {
            return baseValid && modeValid && formData.code && formData.code.length >= 3;
        }
        return baseValid && modeValid;
    };

    return (
        <Box sx={{ maxWidth: 1200, margin: '0 auto', px: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                    <GiftCardIcon sx={{ mr: 1, fontSize: 32 }} />
                    Gift Cards
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ borderRadius: 20 }}
                >
                    Add Gift Card
                </Button>
            </Box>

            <Paper elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="fullWidth"
                    sx={{ 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            minWidth: { xs: 0, sm: 'auto' },
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                        }
                    }}
                >
                    <Tab label="Active" />
                    <Tab label="Redeemed" />
                    <Tab label="Expired" />
                </Tabs>

                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5', display: { xs: 'none', md: 'table-header-group' } }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Valid Until</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Redeemable</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Redeemed By</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCards.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">
                                            No gift cards found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCards.map((card) => (
                                    <React.Fragment key={card.id}>
                                        {/* Desktop View */}
                                        <TableRow 
                                            hover 
                                            sx={{ 
                                                display: { xs: 'none', md: 'table-row' },
                                                cursor: 'default'
                                            }}
                                        >
                                            <TableCell>{card.name}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                                                    >
                                                        {card.code}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyCode(card.code);
                                                        }}
                                                    >
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 'bold', color: '#2ecc71' }}>
                                                    {formatMoney(card.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(card.validUntil).toLocaleDateString('en-US')}
                                            </TableCell>
                                            <TableCell>{getStatusChip(card)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {getRedeemablePlansLabel(card)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {card.redeemedBy ? (
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {card.redeemer?.fullName || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(card.redeemedAt).toLocaleDateString('en-US')}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                    {card.status === 'active' && (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenDialog(card);
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(card.id);
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>

                                        {/* Mobile View */}
                                        <TableRow
                                            onClick={() => handleRowClick(card.id)}
                                            sx={{
                                                display: { xs: 'table-row', md: 'none' },
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {card.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                                            {card.code}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#2ecc71' }}>
                                                            {formatMoney(card.amount)}
                                                        </Typography>
                                                        <IconButton size="small">
                                                            {expandedRow === card.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>

                                        {/* Mobile Expanded Details */}
                                        <TableRow sx={{ display: { xs: 'table-row', md: 'none' } }}>
                                            <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <Collapse in={expandedRow === card.id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 2 }}>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={6}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Code
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                                        {card.code}
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleCopyCode(card.code);
                                                                        }}
                                                                    >
                                                                        <CopyIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Status
                                                                </Typography>
                                                                <Box>{getStatusChip(card)}</Box>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Redeemable
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {getRedeemablePlansLabel(card)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Valid Until
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {new Date(card.validUntil).toLocaleDateString('en-US')}
                                                                </Typography>
                                                            </Grid>
                                                            {card.redeemedBy && (
                                                                <Grid item xs={6}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Redeemed By
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        {card.redeemer?.fullName || 'Unknown'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {new Date(card.redeemedAt).toLocaleDateString('en-US')}
                                                                    </Typography>
                                                                </Grid>
                                                            )}
                                                            {card.status === 'active' && (
                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="primary"
                                                                            startIcon={<EditIcon />}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleOpenDialog(card);
                                                                            }}
                                                                            fullWidth
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="error"
                                                                            startIcon={<DeleteIcon />}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDelete(card.id);
                                                                            }}
                                                                            fullWidth
                                                                        >
                                                                            Delete
                                                                        </Button>
                                                                    </Box>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Gift Card' : 'Add New Gift Card'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Gift Card Name"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="redemption-mode-label">Gift card type</InputLabel>
                            <Select
                                labelId="redemption-mode-label"
                                value={formData.redemptionMode}
                                label="Gift card type"
                                onChange={(e) => setFormData({
                                    ...formData,
                                    redemptionMode: e.target.value,
                                    selectedPlanId: e.target.value === 'amount' ? '' : formData.selectedPlanId
                                })}
                            >
                                <MenuItem value="">
                                    <em>Select type</em>
                                </MenuItem>
                                <MenuItem value="amount">Amount</MenuItem>
                                <MenuItem value="plan">Plan</MenuItem>
                            </Select>
                        </FormControl>

                        {formData.redemptionMode === 'amount' && (
                            <TextField
                                label={`Amount (${paymentCurrency})`}
                                type="number"
                                fullWidth
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                InputProps={{
                                    startAdornment: <Box component="span" sx={{ mr: 1 }}>{paymentCurrency}</Box>
                                }}
                            />
                        )}

                        {formData.redemptionMode === 'plan' && (
                            <FormControl fullWidth>
                                <InputLabel id="single-redeemable-plan-label">Redeemable plan</InputLabel>
                                <Select
                                    labelId="single-redeemable-plan-label"
                                    value={formData.selectedPlanId}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        selectedPlanId: Number(e.target.value)
                                    })}
                                    label="Redeemable plan"
                                >
                                    {plans.map((plan) => (
                                        <MenuItem key={plan.id} value={plan.id}>
                                            {plan.name} - {plan.sessions} sessions - {formatMoney(plan.price)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <TextField
                            label="Valid Until"
                            type="date"
                            fullWidth
                            value={formData.validUntil}
                            onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />

                        <Typography variant="caption" color="text.secondary">
                            Amount card adds credit. Plan card redeems one specific training plan.
                        </Typography>
                        
                        {!editMode && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.autoGenerateCode}
                                            onChange={(e) => setFormData({ 
                                                ...formData, 
                                                autoGenerateCode: e.target.checked,
                                                code: e.target.checked ? '' : formData.code
                                            })}
                                        />
                                    }
                                    label="Auto-generate code"
                                />
                                
                                {!formData.autoGenerateCode && (
                                    <TextField
                                        label="Gift Card Code"
                                        fullWidth
                                        value={formData.code}
                                        onChange={(e) => setFormData({ 
                                            ...formData, 
                                            code: e.target.value.toUpperCase() 
                                        })}
                                        placeholder="Enter custom code"
                                        inputProps={{ 
                                            style: { 
                                                fontFamily: 'monospace', 
                                                textTransform: 'uppercase' 
                                            } 
                                        }}
                                        helperText="Code must be at least 3 characters"
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isFormValid()}
                    >
                        {editMode ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GiftCardManagement;

